import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useOrganizations, useBoardTypes, useCampaignTemplates } from '../data';

import { Wrapper } from '../../../shared/ModalComponents';
import Select from '../../../shared/Select/Select.jsx';
import {
  Button,
  BaseInput,
  Label as BaseLabel,
  StyledSelect,
} from '../../../shared/FormStyles';

import { SelectWrapper as SettingsSelectWrapper } from '../../../shared/SettingsModal/StyledComponents';

const Input = styled(BaseInput)`
  margin: 0 0 16px 0 !important;
`;

const Label = styled(BaseLabel)`
  margin-bottom: 8px;
`;

const SelectWrapper = styled.div`
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-weight: bold;
`;

const ActionsWrapper = styled.div`
  margin: 35px 0 0;
  display: flex;
  justify-content: flex-end;
`;

const formInitialValues = {
  board: {
    name: '',
    organization_id: '',
    customer_status: '',
    campaigns: [],
  },
};

const validationSchema = Yup.object({
  board: Yup.object({
    name: Yup.string().required(),
    organization_id: Yup.string().required(),
    customer_status: Yup.string().required(),
  }),
});

const CreateBoardForm = ({ mutate }) => {
  const { organizations } = useOrganizations();
  const { boardTypes } = useBoardTypes();
  const { campaigns } = useCampaignTemplates();
  const { t } = useTranslation(['header', 'shared']);

  const submittingButtonText = () => (
    <div>
      <i className="fa-solid fa-spinner fa-spin"></i>{' '}
      {t('header:createBoardForm.creating')}
    </div>
  );

  return (
    <Wrapper>
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={values => {
          const params = {
            board: {
              ...values.board,
              campaigns: values.board.campaigns.map(c => c.value),
            },
          };

          mutate({ data: params });
        }}
      >
        {formik => (
          <Form>
            <Title>{t('header:createBoardForm.title')}</Title>
            <h3>{t('header:createBoardForm.header')}</h3>
            <Label htmlFor="board_name">
              {t('header:createBoardForm.boardName')}
            </Label>

            <Input
              type="text"
              id="board_name"
              name="board[name]"
              placeholder={t('header:createBoardForm.nameYourBoard')}
              onChange={formik.handleChange}
              value={formik.values.board.name}
            />
            <SelectWrapper>
              <Label htmlFor="board_organization_id">
                {t('header:createBoardForm.customer')}
              </Label>
              <Select
                id="board_organization_id"
                name="board[organization_id]"
                placeholder={t('header:createBoardForm.selectCustomer')}
                options={organizations}
                className="react-select"
                value={organizations.find(
                  value => value === formik.values.board.organization_id,
                )}
                onChange={option => {
                  formik.setFieldValue('board[organization_id]', option.value);
                }}
                isSearchable={false}
              />
            </SelectWrapper>
            <SelectWrapper>
              <Label htmlFor="board_customer_status">
                {t('createBoardForm.boardType')}
              </Label>
              <Select
                id="board_customer_status"
                name="board[customer_status]"
                placeholder={t('header:createBoardForm.selectType')}
                options={boardTypes}
                className="react-select"
                value={boardTypes.find(
                  value => value === formik.values.board.customer_status,
                )}
                onChange={option => {
                  formik.setFieldValue('board[customer_status]', option.value);
                }}
                isSearchable={false}
              />
            </SelectWrapper>
            <SelectWrapper>
              <SettingsSelectWrapper>
                <Label>{t('header:createBoardForm.campaigns')}</Label>
                <StyledSelect
                  id="board_campaigns"
                  name="board[campaigns]"
                  onChange={options =>
                    formik.setFieldValue('board[campaigns]', options)
                  }
                  className="audience-filter"
                  classNamePrefix="react-select"
                  placeholder={t('header:createBoardForm.addCampaigns')}
                  value={formik.values.board.campaigns}
                  isClearable={true}
                  options={campaigns}
                  isSearchable={true}
                  isMulti={true}
                />
              </SettingsSelectWrapper>
            </SelectWrapper>
            <ActionsWrapper id="newBoardActions">
              <Button
                variant="tertiary"
                type="button"
                onClick={() => Swal.close()}
              >
                {t('shared:cancel')}
              </Button>
              <Button
                variant="containedBlue"
                ml="8px"
                type="submit"
                disabled={
                  formik.isSubmitting || !formik.isValid || !formik.dirty
                }
              >
                {formik.isSubmitting
                  ? submittingButtonText()
                  : t('header:createBoardForm.create')}
              </Button>
            </ActionsWrapper>
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
};

export default CreateBoardForm;
